import { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import { Listbox } from '@headlessui/react';
import useClinicGroup from 'hooks/useClinicGroup';

interface UnitDurationDropdownProps {
  control: any;
  subdomain: string | null;
  clinicGroupId: string;
  onChange?: (clinicGroupId: string) => void;
}

const ClinicGroupDropdown: FC<UnitDurationDropdownProps> = ({
  control,
  subdomain,
  clinicGroupId,
  onChange,
}) => {
  const { isLoading, data: clinicGroupData = [] } = useClinicGroup();

  const options = useMemo(() => {
    return clinicGroupData.map((item: any) => ({
      label: item.name,
      value: item.id,
      slug: item.slug,
      existingSubdomains: item.existingSubdomains,
    }));
  }, [clinicGroupData]);

  const selectedOption = useMemo(() => {
    let result = options.find((item: any) => {
      return item.value === clinicGroupId;
    });

    if (!result) {
      result = options.find((item: any) =>
        item.existingSubdomains.includes(subdomain)
      );
    }

    if (!result && subdomain !== '') {
      result = options.find((item: any) => item.slug === 'others');
    }

    return result;
  }, [clinicGroupId, options, subdomain]);

  if (clinicGroupData.length === 0 || isLoading) {
    return null;
  }

  const label = selectedOption?.label || null;

  return (
    <Controller
      control={control}
      rules={{ required: 'This field is required' }}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Listbox {...field}>
              {({ open }) => {
                return (
                  <div className="relative text-14 leading-[2.1rem] mt-0.8">
                    <Listbox.Button
                      className={`relative w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                        open ? 'border-magenta shadow-input-active' : ''
                      }`}
                    >
                      <span
                        className={`block pr-3 ${
                          label ? 'text-darkest-grey' : 'text-mid-grey'
                        }`}
                      >
                        {label || 'Select Group Name'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <DropdownIcon className="w-1.4 h-1.4" />
                      </span>
                    </Listbox.Button>
                    <Listbox.Options className="absolute mt-0.5 max-h-16 scrollbar w-full overflow-auto rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-10">
                      {options?.map((option: any) => (
                        <Listbox.Option
                          key={option.value}
                          className="relative cursor-default select-none pl-1.6"
                          value={option.value}
                        >
                          <span
                            className={`block truncate cursor-pointer hover:text-magenta ${
                              clinicGroupId === option.value
                                ? 'text-magenta'
                                : ''
                            }`}
                          >
                            {option.label}
                          </span>
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                );
              }}
            </Listbox>
            {error?.message && (
              <div className={`flex items-center gap-x-1 mt-0.8`}>
                <WarningIcon />
                <span className="text-12 text-red-01">{error.message}</span>
              </div>
            )}
          </>
        );
      }}
      name={'clinicGroupId'}
    />
  );
};

export default ClinicGroupDropdown;
